import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  SUPPORTED_LANGUAGES,
  POPULAR_LANGUAGES,
  getRecentLanguages,
  addRecentLanguage,
  type Language,
} from '@/lib/languages';
import { useLanguage } from '@/i18n/LanguageContext';

interface LanguageSelectorProps {
  value: string;
  onValueChange: (code: string) => void;
  includeAutoDetect?: boolean;
  className?: string;
  placeholder?: string;
}

const LanguageSelector = ({
  value,
  onValueChange,
  includeAutoDetect = false,
  className,
  placeholder,
}: LanguageSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  const recentCodes = useMemo(() => (open ? getRecentLanguages() : []), [open]);

  const recentLanguages = useMemo(
    () => recentCodes
      .map(code => SUPPORTED_LANGUAGES.find(l => l.code === code))
      .filter((l): l is Language => !!l),
    [recentCodes]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return SUPPORTED_LANGUAGES;
    const q = search.toLowerCase();
    return SUPPORTED_LANGUAGES.filter(
      l => l.name.toLowerCase().includes(q) || l.nativeName.toLowerCase().includes(q) || l.code.includes(q)
    );
  }, [search]);

  const handleSelect = useCallback((code: string) => {
    onValueChange(code);
    if (code !== 'auto') addRecentLanguage(code);
    setOpen(false);
    setSearch('');
  }, [onValueChange]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (value === 'auto') return t('text.autoDetect');
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === value);
    return lang ? `${lang.nativeName} (${lang.name})` : value;
  }, [value, t]);

  const showSections = !search.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between h-10 sm:h-9 text-sm font-normal', className)}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={t('language.search') || 'Search languages...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {includeAutoDetect && showSections && (
            <button
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                value === 'auto' && 'bg-accent'
              )}
              onClick={() => handleSelect('auto')}
            >
              <Check className={cn('mr-2 h-4 w-4', value === 'auto' ? 'opacity-100' : 'opacity-0')} />
              {t('text.autoDetect')}
            </button>
          )}

          {showSections && recentLanguages.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                {t('language.recent') || 'Recently Used'}
              </div>
              {recentLanguages.map(lang => (
                <LangItem key={`recent-${lang.code}`} lang={lang} selected={value === lang.code} onSelect={handleSelect} />
              ))}
            </>
          )}

          {showSections && (
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {t('language.popular') || 'Popular'}
            </div>
          )}
          {showSections && POPULAR_LANGUAGES.map(lang => (
            <LangItem key={`pop-${lang.code}`} lang={lang} selected={value === lang.code} onSelect={handleSelect} />
          ))}

          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {showSections ? (t('language.all') || 'All Languages') : `${filtered.length} results`}
          </div>
          {filtered.map(lang => (
            <LangItem key={lang.code} lang={lang} selected={value === lang.code} onSelect={handleSelect} />
          ))}
          {filtered.length === 0 && (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              {t('language.noResults') || 'No languages found'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

function LangItem({ lang, selected, onSelect }: { lang: Language; selected: boolean; onSelect: (code: string) => void }) {
  return (
    <button
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
        selected && 'bg-accent'
      )}
      onClick={() => onSelect(lang.code)}
    >
      <Check className={cn('mr-2 h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
      <span className="truncate">{lang.nativeName} ({lang.name})</span>
    </button>
  );
}

export default LanguageSelector;
